import { bulkImportClauses } from "@/api";

import { DialogMutationError } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationError";
import { DialogMutationPending } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationPending";
import { DialogMutationSuccess } from "@/components/shared/dialogs/dialog-mutation-states/DialogMutationSuccess";
import { DialogFormContainer } from "@/components/shared/dialogs/DialogFormContainer";
import { Stack, Typography } from "@mui/material";
import { FormContainer } from "@rhf-kit/mui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PreprocessError } from "./PreprocessError";
import { PreprocessForm } from "./PreprocessForm";
import { PreprocessResult } from "./PreprocessResult";

interface IFormData {
    file?: File;
    objectName?: string;
}

interface IProps {
    /**
     * Callback to close the dialog
     */
    onClose: () => void;
}

/**
 * PreprocessStep component
 */
export const PreprocessStep = ({ onClose }: IProps) => {
    const [confirmed, setConfirmed] = useState(false);

    const queryClient = useQueryClient();
    const {
        mutate: preprocessClauses,
        isPending,
        isError,
        error,
        isSuccess,
        data,
    } = useMutation({
        mutationFn: ({
            objectName,
            preprocessOnly,
        }: {
            objectName: string;
            preprocessOnly: boolean;
        }) => bulkImportClauses(objectName, preprocessOnly),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clauses"],
            });
        },
    });

    const defaultValues: IFormData = {
        file: undefined,
        objectName: undefined,
    };

    const handleSuccess = (formData: IFormData) => {
        if (!formData.objectName) return;

        if (data) {
            setConfirmed(true);
        }

        preprocessClauses({
            objectName: formData.objectName,
            preprocessOnly: !data,
        });
    };

    const renderForm = () => (
        <>
            {isPending && <DialogMutationPending />}

            <Stack width={"100%"} gap={2}>
                <Stack alignItems={"center"}>
                    <Typography variant="h6" fontWeight={600}>
                        Bulk Import
                    </Typography>

                    {!isPending && (
                        <Typography variant={"body2"} color={"textSecondary"}>
                            Upload a file to import clauses
                        </Typography>
                    )}
                </Stack>
                {isError && <PreprocessError error={error} />}
            </Stack>

            {!isPending && !isSuccess && <PreprocessForm onClose={onClose} />}
        </>
    );

    const hideForm = isError && confirmed;

    return (
        <DialogFormContainer maxWidth={"md"}>
            <FormContainer
                defaultValues={defaultValues}
                onSuccess={handleSuccess}
            >
                <Stack
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    gap={8}
                >
                    {!isSuccess && !hideForm && renderForm()}
                    {isSuccess && !confirmed && (
                        <PreprocessResult results={data} onClose={onClose} />
                    )}

                    {isSuccess && confirmed && (
                        <DialogMutationSuccess
                            description="The clauses have been imported successfully."
                            onClose={onClose}
                        />
                    )}

                    {isError && confirmed && (
                        <DialogMutationError
                            subject="Clauses Import"
                            description="An error occurred while importing the clauses."
                            onClose={onClose}
                        />
                    )}
                </Stack>
            </FormContainer>
        </DialogFormContainer>
    );
};
